/**
 * The API store.
 *
 * File: \src\stores\ApiStore.ts
 * Created Date: Monday, July 15th 2024, 14:19:32
 * Author: Herrmann Sascha (LDC)
 * -----
 * Last Modified: April 16th 2025, 14:49:12
 * Modified By: Lazarova Vanya (HAU-DCE)
 * -----
 * Copyright (c) 2024-2024 Liebherr-Digital Development Center GmbH
 */

import { defineStore } from 'pinia';
import { Configuration, ConfigurationParameters, Middleware, RequestContext, FetchParams } from '@/api/runtime';

import { FactoryFloorControllerApi } from '@/api/apis';

import { useLoginStore } from './LoginStore';

/**
 * This middleware adds the access token to every request.
 * @export
 * @implements {Middleware}
 */
class BusMiddleware implements Middleware {
  private context: string = undefined;

  /**
   * Sets the context. That is sent on every request. You can only set this once.
   *
   * @param {string} context The new context.
   * @memberof BusMiddleware
   */
  public setContext(context: string): void {
    if (context && !this.context) {
      this.context = context;
    }
  }

  /**
   * Called after the https call. Not used in this case.
   *
   * @param {any} response The response context.
   * @returns {Promise<void>} Nothing.
   *
   * @memberof BusMiddleware
   */
  public async post(response: any): Promise<any | void> {
    return response.response;
  }

  public async onError(context): Promise<any | void> {
    if (context.response) {
      return context.response;
    }

    throw context.error;
  }
}

/**
 * Interface for the storage of the APIs plus authentication.
 *
 * @export
 * @interface ApiStoreState
 */
interface ApiStoreState {
  busMiddleWare: BusMiddleware;
}

export const useApiStore = defineStore('apiStore', {
  /**
   * The initial state.
   *
   */
  state: (): ApiStoreState => ({
    busMiddleWare: new BusMiddleware()
  }),

  getters: {
    getFactoryFloorControllerApi(): () => FactoryFloorControllerApi {
      return (): FactoryFloorControllerApi => {
        const configuration: Configuration = this.getConfiguration();
        const api: FactoryFloorControllerApi = new FactoryFloorControllerApi(configuration);

        return api;
      };
    },

    /**
     * Gets the authorization for the call.
     *
     * @returns {Configuration} The OAuth object with the access token coming from the loginStore.
     */
    getConfiguration(): () => Configuration {
      return (): Configuration => {
        const configurationParams: ConfigurationParameters = this.getConfigurationParameters();
        const configuration: Configuration = new Configuration(configurationParams);

        return configuration;
      };
    },

    /**
     * Separate getter for the configuration parameters. This allows to add or modify them.
     *
     * @returns {ConfigurationParameters}
     */
    getConfigurationParameters(): () => ConfigurationParameters {
      return (): ConfigurationParameters => {
        const loginStore = useLoginStore();
        const accessToken: string = loginStore.bearerToken();

        const configurationParams: ConfigurationParameters = {
          basePath: window.RUNTIME_CONFIG.VITE_REST_URL,
          middleware: [this.busMiddleWare],
          headers: {
            Authorization: 'Bearer ' + accessToken
          }
        };
        return configurationParams;
      };
    }
  },

  actions: {
    /**
     * Set a user context object. Just calls the mutation. The context is encrypted information.
     *
     * @param {string} context The context to store.
     */
    setContext(context: string): void {
      this.storeContext(context);
    },

    /**
     * Stores the context in the middleware. Can be only done once.
     *
     * @param {string} context The context to store.
     */
    storeContext(context: string): void {
      this.busMiddleWare.setContext(context);
    }
  }
});
